import { DocumentNodeType, DocumentPermission, DocumentStatus, FileTypeCategory } from '../enums/data.enums';
import { DocumentNodeModel } from '../models/data/document.model';
import {
    DOCUMENT_FILE_TYPE_LABELS,
    DOCUMENT_PERMISSION_BADGE_CLASSES,
    DOCUMENT_PERMISSION_ICON_COLOR_CLASSES,
    DOCUMENT_PERMISSION_ICONS,
    DOCUMENT_PERMISSION_LABELS,
    DOCUMENT_STATUS_BADGE_CLASSES,
    DOCUMENT_STATUS_LABELS
} from '../constants/display-labels.constants';
import { DocumentsConstants } from '../constants/documents.constants';

/**
 * Shared documents-domain helpers for rendering a folder's permission (label/icon/badge/color) and
 * node predicates. An undefined permission (e.g. the root header) returns neutral defaults.
 */
export class DocumentUtilities {

    public static permissionLabel(level: DocumentPermission | undefined): string {
        return level === undefined ? '' : DOCUMENT_PERMISSION_LABELS[level];
    }

    public static permissionIcon(level: DocumentPermission | undefined): string {
        return level === undefined ? 'folder' : DOCUMENT_PERMISSION_ICONS[level];
    }

    public static permissionBadgeClass(level: DocumentPermission | undefined): string {
        return DOCUMENT_PERMISSION_BADGE_CLASSES[level ?? DocumentPermission.AdminOnly];
    }

    public static permissionIconColorClass(level: DocumentPermission | undefined): string {
        return level === undefined ? 'text-secondary' : DOCUMENT_PERMISSION_ICON_COLOR_CLASSES[level];
    }

    /** Only top-level folders (no parent) own an editable permission; descendants inherit it. */
    public static isTopLevelFolder(node: DocumentNodeModel): boolean {
        return node.type === DocumentNodeType.Folder && node.parentId == null;
    }

    /** True for the locked, root-level, exact-name-matched "Email Attachments" system folder (DEV-2559). */
    public static isEmailAttachmentsFolder(node: DocumentNodeModel): boolean {
        return DocumentUtilities.isTopLevelFolder(node)
            && !!node.isLocked
            && node.name.toLowerCase() === DocumentsConstants.EMAIL_ATTACHMENTS_FOLDER_NAME().toLowerCase();
    }

    /**
     * Documents list Type column (DEV-2559): "Folder" for folders; a file's extension uppercased
     * without the dot (".pdf" -> "PDF"), falling back to the FileTypeCategory label when the extension
     * is missing.
     */
    public static fileTypeLabel(node: DocumentNodeModel): string {
        if (node.type === DocumentNodeType.Folder) return 'Folder';
        const extension = node.extension?.trim();
        if (extension && extension.length > 1) return extension.replace(/^\./, '').toUpperCase();
        return DOCUMENT_FILE_TYPE_LABELS[node.fileTypeCategory ?? FileTypeCategory.Other];
    }

    public static statusLabel(status: DocumentStatus | undefined): string {
        return status === undefined ? '' : DOCUMENT_STATUS_LABELS[status];
    }

    public static statusBadgeClass(status: DocumentStatus | undefined): string {
        return status === undefined ? '' : DOCUMENT_STATUS_BADGE_CLASSES[status];
    }

    /**
     * A file is downloadable once its scan clears (Available). Folders and legacy files that predate
     * scanning carry no status — treat the absence as available so they stay clickable.
     */
    public static isFileAvailable(node: DocumentNodeModel): boolean {
        return node.status === undefined || node.status === DocumentStatus.Available;
    }

    /**
     * Only the abnormal scan states (Pending / Quarantined) earn a badge — Available is the expected
     * state and is implied by the absence of one.
     */
    public static showStatusBadge(node: DocumentNodeModel): boolean {
        return node.type === DocumentNodeType.File
            && (node.status === DocumentStatus.Pending || node.status === DocumentStatus.Quarantined);
    }

    /**
     * Font Awesome glyph for a file's type category. Shared by the documents grid and the reusable
     * document file picker so the two stay visually in sync.
     */
    public static fileTypeIcon(category: FileTypeCategory | undefined): string {
        switch (category) {
            case FileTypeCategory.Pdf: return 'file-pdf';
            case FileTypeCategory.Image: return 'file-image';
            case FileTypeCategory.Document: return 'file-word';
            case FileTypeCategory.Spreadsheet: return 'file-spreadsheet';
            case FileTypeCategory.Video: return 'file-video';
            case FileTypeCategory.Archive: return 'file-zipper';
            default: return 'file';
        }
    }

    /** Whether a node is an image file — drives thumbnail rendering in file pickers/grids. */
    public static isImageFile(node: DocumentNodeModel): boolean {
        return node.type === DocumentNodeType.File && node.fileTypeCategory === FileTypeCategory.Image;
    }

    /**
     * Format a byte count as a compact human-readable size (B / KB / MB / GB). Undefined/NaN yields
     * an empty string; fractional digits shrink as the unit grows so values stay short.
     */
    public static formatBytes(bytes: number | undefined): string {
        if (bytes === undefined || bytes === null || Number.isNaN(bytes)) return '';
        if (bytes < 1024) return `${bytes} B`;
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
        const mb = kb / 1024;
        if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
        const gb = mb / 1024;
        return `${gb.toFixed(2)} GB`;
    }

    /**
     * Split a filename into its base and extension (the extension includes the dot, e.g. ".pdf").
     * A name with no dot, or a leading-dot dotfile, has no extension.
     */
    public static splitFileName(name: string): { base: string; extension: string } {
        const lastDot = name.lastIndexOf('.');
        if (lastDot <= 0) {
            return { base: name, extension: '' };
        }
        return { base: name.slice(0, lastDot), extension: name.slice(lastDot) };
    }
}
